import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { VendorEmailsState, VendorEmail } from '../../types';
import { reviewService } from '../../services/reviewService';

const initialState: VendorEmailsState = {
  emails: {},
  isGenerating: {},
  isLoading: {},
  error: null,
};

// Async thunks
export const generateVendorEmails = createAsyncThunk(
  'vendorEmails/generate',
  async ({ reviewId, feedback }: { reviewId: number; feedback?: Record<number, string> }) => {
    const result = await reviewService.generateVendorEmails(reviewId, feedback);
    return { reviewId, result };
  }
);

export const fetchVendorEmails = createAsyncThunk(
  'vendorEmails/fetch',
  async (reviewId: number) => {
    const result = await reviewService.getVendorEmails(reviewId);
    return { reviewId, emails: result.emails };
  }
);

export const updateVendorEmailStatus = createAsyncThunk(
  'vendorEmails/updateStatus',
  async ({ 
    reviewId, 
    vendorTypeId, 
    status 
  }: { 
    reviewId: number; 
    vendorTypeId: number; 
    status: 'sent' | 'dismissed' 
  }) => {
    await reviewService.updateVendorEmailStatus(reviewId, vendorTypeId, status);
    return { reviewId, vendorTypeId, status };
  }
);

export const updateVendorEmailContent = createAsyncThunk(
  'vendorEmails/updateContent',
  async ({ 
    reviewId, 
    vendorTypeId, 
    subject,
    content 
  }: { 
    reviewId: number; 
    vendorTypeId: number; 
    subject: string;
    content: string;
  }) => {
    await reviewService.updateVendorEmailContent(reviewId, vendorTypeId, subject, content);
    return { reviewId, vendorTypeId, subject, content };
  }
);

export const sendVendorEmail = createAsyncThunk(
  'vendorEmails/send',
  async ({ 
    reviewId, 
    vendorTypeId,
    vendorIds 
  }: { 
    reviewId: number; 
    vendorTypeId: number;
    vendorIds?: number[]; 
  }) => {
    const result = await reviewService.sendVendorEmail(reviewId, vendorTypeId, vendorIds);
    return { reviewId, vendorTypeId, reviewCompleted: result.reviewCompleted };
  }
);

const vendorEmailSlice = createSlice({
  name: 'vendorEmails',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearVendorEmails: (state, action: PayloadAction<number>) => {
      const reviewId = action.payload;
      delete state.emails[reviewId];
      delete state.isGenerating[reviewId];
      delete state.isLoading[reviewId];
    },
  },
  extraReducers: (builder) => {
    builder
      // Generate vendor emails
      .addCase(generateVendorEmails.pending, (state, action) => {
        const reviewId = action.meta.arg.reviewId;
        state.isGenerating[reviewId] = true;
        state.error = null;
      })
      .addCase(generateVendorEmails.fulfilled, (state, action) => {
        const { reviewId, result } = action.payload;
        state.isGenerating[reviewId] = false;
        
        // The result contains generatedEmails and errors
        if (result && result.generatedEmails && Array.isArray(result.generatedEmails)) {
          // Ensure the emails object exists
          if (!state.emails) {
            state.emails = {};
          }
          state.emails[reviewId] = result.generatedEmails;
        }
        
        if (result && result.errors && result.errors.length > 0) {
          state.error = `Failed to generate ${result.errors.length} emails`;
        }
      })
      .addCase(generateVendorEmails.rejected, (state, action) => {
        const reviewId = action.meta.arg.reviewId;
        state.isGenerating[reviewId] = false;
        state.error = action.error.message || 'Failed to generate vendor emails';
      })
      
      // Fetch vendor emails
      .addCase(fetchVendorEmails.pending, (state, action) => {
        const reviewId = action.meta.arg;
        state.isLoading[reviewId] = true;
        state.error = null;
      })
      .addCase(fetchVendorEmails.fulfilled, (state, action) => {
        const { reviewId, emails } = action.payload;
        state.isLoading[reviewId] = false;
        state.emails[reviewId] = emails;
      })
      .addCase(fetchVendorEmails.rejected, (state, action) => {
        const reviewId = action.meta.arg;
        state.isLoading[reviewId] = false;
        state.error = action.error.message || 'Failed to fetch vendor emails';
      })
      
      // Update vendor email status
      .addCase(updateVendorEmailStatus.fulfilled, (state, action) => {
        const { reviewId, vendorTypeId, status } = action.payload;
        const emails = state.emails[reviewId];
        
        if (emails) {
          const emailIndex = emails.findIndex(email => email.vendorTypeId === vendorTypeId);
          if (emailIndex !== -1) {
            emails[emailIndex].status = status;
          }
        }
      })
      .addCase(updateVendorEmailStatus.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update email status';
      })
      
      // Update vendor email content
      .addCase(updateVendorEmailContent.fulfilled, (state, action) => {
        const { reviewId, vendorTypeId, subject, content } = action.payload;
        const emails = state.emails[reviewId];
        
        if (emails) {
          const emailIndex = emails.findIndex(email => email.vendorTypeId === vendorTypeId);
          if (emailIndex !== -1) {
            emails[emailIndex].subject = subject;
            emails[emailIndex].content = content;
          }
        }
      })
      .addCase(updateVendorEmailContent.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update email content';
      })
      
      // Send vendor email
      .addCase(sendVendorEmail.fulfilled, (state, action) => {
        const { reviewId, vendorTypeId } = action.payload;
        const emails = state.emails[reviewId];
        
        if (emails) {
          const emailIndex = emails.findIndex(email => email.vendorTypeId === vendorTypeId);
          if (emailIndex !== -1) {
            emails[emailIndex].status = 'sent';
          }
        }
      })
      .addCase(sendVendorEmail.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to send email';
      });
  },
});

export const { clearError, clearVendorEmails } = vendorEmailSlice.actions;
export default vendorEmailSlice.reducer;